"use client";

import { useState } from "react";
import Link from "next/link";
import NextImage from "next/image";
import { usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { cn, mediaUrl } from "@/lib/utils";
import { useSidebar } from "@/components/layout/sidebar-context";
import {
  LayoutDashboard, Package, FolderTree, ShoppingCart,
  Users, Settings, FileText, Image, Menu, Tag,
  BarChart3, Truck, CreditCard, Paintbrush, Search, Share2, FileCode2, Store,
  BookOpen, TrendingUp, Receipt, User, Zap, Bell, Bot, List, ChevronDown, RotateCcw, Globe, KeyRound,
  ScrollText, Mail, MessageCircle, Smartphone,
} from "lucide-react";

type NavItem = { label: string; href: string; icon: React.ElementType };
type NavGroup = { label: string; items: NavItem[] };
type NavSection = { label: string; items: (NavItem | NavGroup)[] };
type NavEntry = NavItem | NavSection;

const navItems: NavEntry[] = [
  { label: "Ana Sayfa", href: "/dashboard", icon: LayoutDashboard },
  { label: "Uyarılar",  href: "/uyarilar",  icon: Bell },
  {
    label: "Katalog",
    items: [
      { label: "Ürünler", href: "/products", icon: Package },
      { label: "Kategoriler", href: "/categories", icon: FolderTree },
      { label: "Markalar", href: "/brands", icon: Tag },
    ],
  },
  {
    label: "Satış",
    items: [
      { label: "Siparişler", href: "/orders", icon: ShoppingCart },
      { label: "Aktif Sepetler", href: "/carts/active", icon: ShoppingCart },
      { label: "Terk Edilmiş Sepetler", href: "/carts/abandoned", icon: ShoppingCart },
      { label: "Kargolar", href: "/shipments", icon: Truck },
      { label: "İade Talepleri", href: "/returns", icon: RotateCcw },
      { label: "Müşteriler", href: "/customers", icon: Users },
      { label: "Kuponlar", href: "/coupons", icon: Tag },
    ],
  },
  {
    label: "Tedarikçiler",
    items: [
      { label: "Kaynaklar", href: "/suppliers", icon: Store },
      { label: "Fiyat Kuralları", href: "/supplier-price-rules", icon: Tag },
    ],
  },
  {
    label: "Bayiler",
    items: [
      { label: "Bayi Grupları", href: "/dealers/groups", icon: Users },
      { label: "Başvurular", href: "/dealers/applications", icon: FileText },
      { label: "Onaylı Bayiler", href: "/dealers", icon: ShoppingCart },
    ],
  },
  {
    label: "İçerik",
    items: [
      { label: "Sayfalar", href: "/content/pages", icon: FileText },
      { label: "Menüler", href: "/content/menus", icon: Menu },
      { label: "Sliderlar", href: "/content/sliders", icon: Image },
      { label: "Ürün Sayfası", href: "/settings/product-page", icon: Settings },
      { label: "Tema", href: "/settings/theme", icon: Paintbrush },
    ],
  },
  {
    label: "Sözleşmeler",
    items: [
      { label: "Sözleşmeler", href: "/contracts", icon: ScrollText },
      { label: "Ortak Değişkenler", href: "/contracts/variables", icon: Settings },
    ],
  },
  {
    label: "Mesajlar",
    items: [
      { label: "Mail Ayarları", href: "/settings/mail", icon: Mail },
      { label: "SMS Ayarları", href: "/settings/sms", icon: Smartphone },
      { label: "Whatsapp Ayarları", href: "/settings/whatsapp", icon: MessageCircle },
      { label: "Bildirim Kanalları", href: "/settings/notifications", icon: Bell },
      { label: "Mesaj Şablonları", href: "/message-templates", icon: FileCode2 },
    ],
  },
  {
    label: "Entegrasyonlar",
    items: [
      {
        label: "Pazaryerleri",
        items: [
          { label: "Pazaryerleri", href: "/integrations", icon: Store },
          { label: "Pazaryeri Dashboard", href: "/integrations/dashboard", icon: LayoutDashboard },
          { label: "Pazaryeri Kuyruğu", href: "/integrations/queue", icon: List },
          { label: "Pazaryeri Eşleştirmeleri", href: "/integrations/mappings", icon: Package },
          { label: "Trendyol", href: "/integrations/trendyol", icon: Store },
          { label: "Trendyol Kayıtları", href: "/integrations/trendyol/logs", icon: FileText },
          { label: "n11", href: "/integrations/n11", icon: Store },
          { label: "n11 Kayıtları", href: "/integrations/n11/logs", icon: FileText },
          { label: "Hepsiburada", href: "/integrations/hepsiburada", icon: Store },
          { label: "Hepsiburada Kayıtları", href: "/integrations/hepsiburada/logs", icon: FileText },
        ],
      },
      {
        label: "Ödeme",
        items: [
          { label: "Ödeme", href: "/settings/payment", icon: CreditCard },
          { label: "E-Fatura", href: "/accounting/e-invoice", icon: Zap },
          { label: "E-Fatura Kuyruğu", href: "/accounting/e-invoice/queue", icon: List },
        ],
      },
      {
        label: "Kargo",
        items: [
          { label: "Kargo Entegrasyonları", href: "/settings/shipping", icon: Truck },
        ],
      },
    ],
  },
  {
    label: "Raporlar",
    items: [
      { label: "Satış Raporu", href: "/reports/sales", icon: BarChart3 },
    ],
  },
  {
    label: "Muhasebe",
    items: [
      { label: "Genel Bakış",   href: "/accounting/overview",          icon: BookOpen },
      { label: "Gelir Tablosu", href: "/accounting/income-statement",  icon: TrendingUp },
      { label: "KDV Raporu",    href: "/accounting/vat-report",        icon: Receipt },
      { label: "Faturalar",     href: "/accounting/invoices",          icon: FileText },
      { label: "Ayarlar",       href: "/accounting/settings",          icon: Settings },
    ],
  },
  {
    label: "Ayarlar",
    items: [
      { label: "Yönetici Hesabı", href: "/settings/account", icon: User },
      { label: "Genel", href: "/settings/general", icon: Settings },
      { label: "SEO", href: "/settings/seo", icon: Search },
      { label: "Müşteri Girişi", href: "/settings/auth", icon: KeyRound },
      { label: "Sosyal Medya", href: "/settings/social", icon: Share2 },
      { label: "DNS Kayıtları", href: "/settings/dns", icon: Globe },
    ],
  },
  {
    label: "Yapay Zeka",
    items: [
      { label: "AI API Ayarları", href: "/ai/settings", icon: Bot },
    ],
  },
];

export function Sidebar() {
  const pathname = usePathname();
  const { open, close, collapsed } = useSidebar();
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set());
  const toggleSection = (label: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const { data: config } = useQuery({
    queryKey: ["store-config-public"],
    queryFn: async () => {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL ?? ""}/api/store/config`);
      if (!res.ok) return null;
      return res.json() as Promise<{ general: Record<string, string> }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const faviconUrl = config?.general?.["store.favicon_url"];

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={close}
        />
      )}

      <aside
        className={cn(
          // Base
          "w-64 shrink-0 border-r border-slate-200 bg-sidebar flex flex-col",
          "shadow-[2px_0_8px_rgba(0,0,0,0.04)] dark:border-slate-800 dark:shadow-[2px_0_18px_rgba(0,0,0,0.28)]",
          "lg:transition-[width] lg:duration-300",
          // Desktop: static, always visible
          "lg:relative lg:h-screen lg:translate-x-0 lg:z-10",
          // Mobile: fixed drawer, slides in/out
          "fixed inset-y-0 left-0 h-full z-50 transition-transform duration-300 lg:transition-none",
          collapsed && "lg:w-20",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            "h-16 flex items-center border-b border-slate-200 gap-3 shrink-0",
            "dark:border-slate-800",
            collapsed ? "px-3" : "px-5",
          )}
        >
          <Link
            href="/dashboard"
            onClick={close}
            className={cn(
              "flex items-center gap-3 min-w-0 overflow-hidden rounded-xl transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300",
              collapsed ? "justify-center flex-1" : "flex-1",
            )}
            title="Ana sayfaya dön"
          >
            {faviconUrl ? (
              <NextImage
                src={mediaUrl(faviconUrl)}
                alt="Site ikonu"
                width={28}
                height={28}
                className="h-7 w-7 rounded-lg object-contain shrink-0"
              />
            ) : (
              <NextImage
                src="/hba-icon.png"
                alt="HBA Store"
                width={28}
                height={28}
                className="h-7 w-7 rounded-lg object-contain shrink-0"
              />
            )}
            <div className={cn("flex items-center gap-2 min-w-0", collapsed && "lg:hidden")}>
              <span className="font-bold text-sm tracking-tight truncate text-primary">HBA Store</span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className={cn("flex-1 overflow-y-auto py-3", collapsed ? "px-2" : "px-3")}>
          {navItems.map((section) =>
            "href" in section ? (
              <NavLink
                key={section.href}
                href={section.href}
                icon={section.icon}
                label={section.label}
                pathname={pathname}
                onNavigate={close}
                collapsed={collapsed}
              />
            ) : (
              <div key={section.label} className="mb-3">
                <button
                  type="button"
                  onClick={() => toggleSection(section.label)}
                  aria-expanded={openSections.has(section.label)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-1.5 rounded-[var(--radius)] text-[13px] transition-all duration-200 mb-px cursor-pointer",
                    "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-100",
                    collapsed && "lg:hidden",
                  )}
                >
                  <span className="font-semibold text-[10px] uppercase tracking-widest">
                    {section.label}
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      "h-3.5 w-3.5 shrink-0 transition-transform duration-300",
                      openSections.has(section.label) ? "rotate-180" : "rotate-0"
                    )}
                  />
                </button>
                <div
                  className={cn(
                    "grid transition-all duration-300 ease-in-out",
                    openSections.has(section.label) || collapsed ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  )}
                >
                  <div className="overflow-hidden">
                    {section.items.map((item) => {
                      const sectionOpen = openSections.has(section.label);

                      // Düz bağlantı
                      if ("href" in item) {
                        return (
                          <NavLink
                            key={item.href}
                            href={item.href}
                            icon={item.icon}
                            label={item.label}
                            pathname={pathname}
                            onNavigate={close}
                            collapsed={collapsed}
                            hidden={!sectionOpen && !collapsed}
                          />
                        );
                      }

                      // Alt grup (iç içe açılır-kapanır)
                      const groupKey = `${section.label}::${item.label}`;
                      const groupOpen = openSections.has(groupKey);
                      return (
                        <div key={groupKey} className={cn(!collapsed && "mt-0.5")}>
                          <button
                            type="button"
                            onClick={() => toggleSection(groupKey)}
                            aria-expanded={groupOpen}
                            tabIndex={!sectionOpen && !collapsed ? -1 : undefined}
                            aria-hidden={!sectionOpen && !collapsed ? true : undefined}
                            className={cn(
                              "w-full flex items-center justify-between pl-5 pr-3 py-1.5 rounded-[var(--radius)] text-[13px] transition-all duration-200 mb-px cursor-pointer",
                              "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-100",
                              collapsed && "lg:hidden",
                            )}
                          >
                            <span className="font-medium text-[11px] tracking-wide">{item.label}</span>
                            <ChevronDown
                              aria-hidden="true"
                              className={cn(
                                "h-3 w-3 shrink-0 transition-transform duration-300",
                                groupOpen ? "rotate-180" : "rotate-0"
                              )}
                            />
                          </button>
                          <div
                            className={cn(
                              "grid transition-all duration-300 ease-in-out",
                              groupOpen || collapsed ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                            )}
                          >
                            <div className="overflow-hidden">
                              <div className={cn(!collapsed && "ml-5 border-l border-slate-200 pl-1.5 dark:border-slate-800")}>
                                {item.items.map((leaf) => (
                                  <NavLink
                                    key={leaf.href}
                                    href={leaf.href}
                                    icon={leaf.icon}
                                    label={leaf.label}
                                    pathname={pathname}
                                    onNavigate={close}
                                    collapsed={collapsed}
                                    hidden={(!sectionOpen || !groupOpen) && !collapsed}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className={cn("px-4 py-3 border-t border-slate-200 shrink-0 dark:border-slate-800", collapsed && "lg:px-2")}>
          <p className={cn("text-[10px] text-slate-400 text-center", collapsed && "lg:hidden")}>
            v{process.env.NEXT_PUBLIC_APP_VERSION}
          </p>
        </div>
      </aside>
    </>
  );
}

const allNavHrefs = navItems.flatMap((s) =>
  "href" in s
    ? [s.href]
    : s.items.flatMap((i) => ("href" in i ? [i.href] : i.items.map((leaf) => leaf.href)))
);

function NavLink({
  href, icon: Icon, label, pathname, onNavigate, collapsed, hidden,
}: {
  href: string; icon: React.ElementType; label: string; pathname: string; onNavigate: () => void; collapsed: boolean; hidden?: boolean;
}) {
  const isExactMatch = pathname === href;
  const isPrefixMatch = pathname.startsWith(href + "/");

  // Eğer bu nav item'in href'i, başka bir nav item'in parent'ı ise
  // ve pathname o daha spesifik nav item'e aitse, bu item aktif olmamalı
  const isParentOfActiveChild = allNavHrefs.some(
    (other) =>
      other !== href &&
      other.startsWith(href + "/") &&
      (pathname === other || pathname.startsWith(other + "/"))
  );

  const isActive = isExactMatch || (isPrefixMatch && !isParentOfActiveChild);

  return (
    <Link
      href={href}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      aria-current={isActive ? "page" : undefined}
      tabIndex={hidden ? -1 : undefined}
      aria-hidden={hidden ? true : undefined}
      className={cn(
        "flex items-center gap-2.5 px-3 py-1.5 rounded-[var(--radius)] text-[13px] transition-all duration-200 mb-px cursor-pointer",
        collapsed && "lg:justify-center lg:px-0",
        isActive
          ? "bg-sky-50 shadow-sm border border-sky-100 font-semibold text-primary dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-100 dark:shadow-black/20"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-transparent dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-100"
      )}
    >
      <Icon className={cn(
        "h-3.5 w-3.5 shrink-0 transition-colors",
        isActive ? "text-sky-800 dark:text-sky-300" : "text-slate-400 dark:text-slate-500"
      )} />
      <span className={cn("truncate", collapsed && "lg:hidden")}>{label}</span>
    </Link>
  );
}
